
import { supabase } from "@/integrations/supabase/client";
import { FloorPlan } from "@/types/FloorPlan.types";

export const fetchUserFloorPlans = async (userId: string) => {
  console.log('Fetching floor plans for user:', userId);
  const { data, error } = await supabase
    .from("floor_plans")
    .select("*")
    .eq("user_id", userId)
    .order("updated_at", { ascending: false });

  if (error) {
    console.error('Error fetching floor plans:', error);
    throw error;
  }
  
  console.log('Raw floor plans data:', data);
  
  return data?.map(plan => ({
    ...plan,
    is_active: plan.is_active === true,
    is_favorite: !!plan.is_favorite,
    is_public: plan.is_public ?? true
  })) as FloorPlan[];
};

export const updateFloorPlanName = async (planId: string, userId: string, newName: string) => {
  const { error } = await supabase
    .from("floor_plans")
    .update({ 
      name: newName,
      updated_at: new Date().toISOString()
    })
    .eq("id", planId)
    .eq("user_id", userId);

  if (error) throw error;
};

export const duplicateFloorPlan = async (plan: FloorPlan, userId: string) => {
  const { error } = await supabase
    .from("floor_plans")
    .insert({
      name: `${plan.name} (kopie)`,
      content: plan.content,
      user_id: userId,
      preview_image: plan.preview_image,
      is_public: plan.is_public,
      is_active: false,
      is_favorite: false
    });

  if (error) throw error;
};

export const deleteFloorPlan = async (planId: string, userId: string) => {
  const { error } = await supabase
    .from("floor_plans")
    .delete()
    .eq("id", planId)
    .eq("user_id", userId);

  if (error) throw error;
};

export const updateFloorPlanFolder = async (planId: string, userId: string, folderId: string | null) => {
  const { error } = await supabase
    .from("floor_plans")
    .update({ 
      folder_id: folderId,
      updated_at: new Date().toISOString()
    })
    .eq("id", planId)
    .eq("user_id", userId);

  if (error) throw error;
};

export const updateFloorPlanFavorite = async (planId: string, userId: string, favorite: boolean) => {
  const { error } = await supabase
    .from("floor_plans")
    .update({ 
      is_favorite: favorite,
      updated_at: new Date().toISOString()
    })
    .eq("id", planId)
    .eq("user_id", userId);

  if (error) throw error;
};

export const updateFloorPlanVisibility = async (planId: string, userId: string, isPublic: boolean) => {
  const { error } = await supabase
    .from("floor_plans")
    .update({ 
      is_public: isPublic,
      updated_at: new Date().toISOString()
    })
    .eq("id", planId)
    .eq("user_id", userId);

  if (error) throw error;
};

export const createNewFloorPlan = async (userId: string) => {
  try {
    console.log('Creating new floor plan for user:', userId);
    
    // Deactivate all other floor plans
    const { error: deactivateError } = await supabase
      .from("floor_plans")
      .update({ is_active: false })
      .eq("user_id", userId);
      
    if (deactivateError) {
      console.error('Error deactivating floor plans:', deactivateError);
      throw deactivateError;
    }
    
    // Wait for deactivation to be fully processed
    await new Promise(resolve => setTimeout(resolve, 500));

    // Create a new floor plan with is_active: true
    const { data, error } = await supabase
      .from("floor_plans")
      .insert({
        name: "Nieuwe plattegrond",
        content: {},
        user_id: userId,
        is_active: true,
        is_public: true
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating new floor plan:', error);
      throw error;
    }
    
    console.log('New floor plan created successfully:', data);
    
    // Verify the new floor plan is active
    const maxVerifyAttempts = 5;
    let verifyAttempt = 0;
    let isVerified = false;
    
    while (!isVerified && verifyAttempt < maxVerifyAttempts) {
      verifyAttempt++;
      
      // Small delay before checking
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const { data: checkData, error: checkError } = await supabase
        .from("floor_plans")
        .select("is_active")
        .eq("id", data.id)
        .single();
      
      if (!checkError && checkData && checkData.is_active === true) {
        console.log(`Verification successful on attempt ${verifyAttempt}: floor plan is active`);
        isVerified = true;
      } else {
        console.error(`Verification attempt ${verifyAttempt} failed:`, checkError || "Plan not active");
        
        if (verifyAttempt < maxVerifyAttempts) {
          // Try to explicitly set active again
          const { error: reactivateError } = await supabase
            .from("floor_plans")
            .update({ is_active: true })
            .eq("id", data.id)
            .eq("user_id", userId);
          
          if (reactivateError) {
            console.error(`Reactivation attempt ${verifyAttempt} failed:`, reactivateError);
          }
        }
      }
    }
    
    if (!isVerified) {
      console.error('Failed to verify floor plan activation after multiple attempts');
    }
    
    return data;
  } catch (error) {
    console.error('Error in createNewFloorPlan:', error);
    throw error;
  }
};

// Completely rewritten function to ensure only one active floor plan at a time
export const activateFloorPlan = async (planId: string): Promise<boolean> => {
  console.log('Activating floor plan:', planId);
  const userId = (await supabase.auth.getUser()).data.user?.id;
  
  if (!userId) {
    console.error('No user ID available for floor plan activation');
    throw new Error('Gebruiker niet ingelogd');
  }
  
  try {
    // STEP 1: First deactivate ALL floor plans for this user
    console.log(`Deactivating all floor plans for user ${userId}`);
    const { error: deactivateError } = await supabase
      .from("floor_plans")
      .update({ is_active: false })
      .eq("user_id", userId);
    
    if (deactivateError) {
      console.error('Error deactivating all floor plans:', deactivateError);
      throw deactivateError;
    }
    
    // Wait for deactivation to be fully processed
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // STEP 2: Check if any floor plans are still active (sanity check)
    const { data: activeCheck, error: activeCheckError } = await supabase
      .from("floor_plans")
      .select("id")
      .eq("user_id", userId)
      .eq("is_active", true);
      
    if (activeCheckError) {
      console.error('Error checking for active floor plans:', activeCheckError);
    } else if (activeCheck && activeCheck.length > 0) {
      console.error(`WARNING: Found ${activeCheck.length} active floor plans after deactivation attempt`);
      
      // Force deactivate again with a longer timeout
      const { error: forceDeactivateError } = await supabase
        .from("floor_plans")
        .update({ is_active: false })
        .eq("user_id", userId);
        
      if (forceDeactivateError) {
        console.error('Error force deactivating floor plans:', forceDeactivateError);
      } else {
        await new Promise(resolve => setTimeout(resolve, 800));
      }
    }
    
    // STEP 3: Now activate the requested floor plan
    console.log(`Activating floor plan ${planId}`);
    const { error: activateError } = await supabase
      .from("floor_plans")
      .update({ 
        is_active: true,
        updated_at: new Date().toISOString()
      })
      .eq("id", planId)
      .eq("user_id", userId);
    
    if (activateError) {
      console.error('Error activating floor plan:', activateError);
      throw activateError;
    }
    
    // STEP 4: Verify the floor plan is now active
    let verifyAttempt = 0;
    const maxVerifyAttempts = 5;
    let isVerified = false;
    
    while (!isVerified && verifyAttempt < maxVerifyAttempts) {
      verifyAttempt++;
      
      // Wait before checking
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const { data: verifyData, error: verifyError } = await supabase
        .from("floor_plans")
        .select("is_active")
        .eq("id", planId)
        .eq("user_id", userId)
        .single();
      
      if (verifyError) {
        console.error(`Verification attempt ${verifyAttempt} failed:`, verifyError);
      } else if (!verifyData || verifyData.is_active !== true) {
        console.error(`Verification attempt ${verifyAttempt} failed: Plan not active`);
        
        if (verifyAttempt < maxVerifyAttempts) {
          // Try to activate again
          const { error: reactivateError } = await supabase
            .from("floor_plans")
            .update({ is_active: true })
            .eq("id", planId)
            .eq("user_id", userId);
          
          if (reactivateError) {
            console.error(`Reactivation attempt ${verifyAttempt} failed:`, reactivateError);
          }
        }
      } else {
        console.log(`Verification successful on attempt ${verifyAttempt}: Floor plan ${planId} is now active`);
        isVerified = true;
      }
    }
    
    // STEP 5: Final check - are there multiple active floor plans?
    const { data: finalActiveCheck, error: finalCheckError } = await supabase
      .from("floor_plans")
      .select("id, name")
      .eq("user_id", userId)
      .eq("is_active", true);
    
    if (finalCheckError) {
      console.error('Error in final active check:', finalCheckError);
    } else if (finalActiveCheck && finalActiveCheck.length > 1) {
      console.error(`WARNING: Found ${finalActiveCheck.length} active floor plans after activation. Active plans:`, 
        finalActiveCheck.map(p => `${p.id} (${p.name})`).join(', '));
      
      // Emergency fix - deactivate all except the one we want
      const { error: emergencyFixError } = await supabase
        .from("floor_plans")
        .update({ is_active: false })
        .eq("user_id", userId)
        .neq("id", planId);
      
      if (emergencyFixError) {
        console.error('Error in emergency fix:', emergencyFixError);
      } else {
        console.log('Emergency fix applied: Deactivated all other floor plans');
      }
    } else if (finalActiveCheck && finalActiveCheck.length === 1) {
      console.log(`CONFIRMATION: Exactly one floor plan is active: ${finalActiveCheck[0].id} (${finalActiveCheck[0].name})`);
    } else {
      console.error('WARNING: No active floor plans found after activation process!');
      
      // Last resort activation
      const { error: lastResortError } = await supabase
        .from("floor_plans")
        .update({ is_active: true })
        .eq("id", planId)
        .eq("user_id", userId);
      
      if (lastResortError) {
        console.error('Error in last resort activation:', lastResortError);
      } else {
        console.log('Last resort activation completed');
      }
    }
    
    if (!isVerified) {
      console.error(`WARNING: Could not verify floor plan ${planId} activation after ${maxVerifyAttempts} attempts`);
    }
    
    return true;
  } catch (error) {
    console.error('Error in activateFloorPlan:', error);
    throw error;
  }
};

// Completely rewritten function to ensure only one active floor plan during copy
export const copyAndActivateSharedFloorPlan = async (plan: any, userId: string) => {
  console.log('Copying and activating shared floor plan:', plan.id);
  
  try {
    // STEP 1: Deactivate all existing floor plans
    console.log(`Deactivating all floor plans for user ${userId}`);
    const { error: deactivateError } = await supabase
      .from("floor_plans")
      .update({ is_active: false })
      .eq("user_id", userId);
    
    if (deactivateError) {
      console.error('Error deactivating existing floor plans:', deactivateError);
      throw deactivateError;
    }
    
    // Wait for deactivation to be fully processed
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // STEP 2: Check if any floor plans are still active (sanity check)
    const { data: activeCheck, error: activeCheckError } = await supabase
      .from("floor_plans")
      .select("id")
      .eq("user_id", userId)
      .eq("is_active", true);
      
    if (activeCheckError) {
      console.error('Error checking for active floor plans:', activeCheckError);
    } else if (activeCheck && activeCheck.length > 0) {
      console.error(`WARNING: Found ${activeCheck.length} active floor plans after deactivation attempt`);
      
      // Force deactivate again with a longer timeout
      const { error: forceDeactivateError } = await supabase
        .from("floor_plans")
        .update({ is_active: false })
        .eq("user_id", userId);
        
      if (forceDeactivateError) {
        console.error('Error force deactivating floor plans:', forceDeactivateError);
      } else {
        await new Promise(resolve => setTimeout(resolve, 800));
      }
    }
    
    // STEP 3: Create a copy with is_active: true
    console.log('Creating copy of shared floor plan');
    const { data, error } = await supabase
      .from('floor_plans')
      .insert({
        name: `Kopie van ${plan.name}`,
        content: plan.content,
        user_id: userId,
        is_public: false,
        is_active: true,
        preview_image: plan.preview_image
      })
      .select()
      .single();

    if (error) {
      console.error('Error copying shared floor plan:', error);
      throw error;
    }
    
    console.log('Successfully created copy with ID:', data.id);
    
    // STEP 4: Verify the copied floor plan is active
    let verifyAttempt = 0;
    const maxVerifyAttempts = 5;
    let isVerified = false;
    
    while (!isVerified && verifyAttempt < maxVerifyAttempts) {
      verifyAttempt++;
      
      // Wait before checking
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const { data: verifyData, error: verifyError } = await supabase
        .from("floor_plans")
        .select("is_active")
        .eq("id", data.id)
        .eq("user_id", userId)
        .single();
      
      if (verifyError) {
        console.error(`Verification attempt ${verifyAttempt} failed:`, verifyError);
      } else if (!verifyData || verifyData.is_active !== true) {
        console.error(`Verification attempt ${verifyAttempt} failed: Copied plan not active`);
        
        if (verifyAttempt < maxVerifyAttempts) {
          // Try to activate again
          const { error: reactivateError } = await supabase
            .from("floor_plans")
            .update({ is_active: true })
            .eq("id", data.id)
            .eq("user_id", userId);
          
          if (reactivateError) {
            console.error(`Reactivation attempt ${verifyAttempt} failed:`, reactivateError);
          }
        }
      } else {
        console.log(`Verification successful on attempt ${verifyAttempt}: Copied floor plan is active`);
        isVerified = true;
      }
    }
    
    // STEP 5: Final check - are there multiple active floor plans?
    const { data: finalActiveCheck, error: finalCheckError } = await supabase
      .from("floor_plans")
      .select("id, name")
      .eq("user_id", userId)
      .eq("is_active", true);
    
    if (finalCheckError) {
      console.error('Error in final active check:', finalCheckError);
    } else if (finalActiveCheck && finalActiveCheck.length > 1) {
      console.error(`WARNING: Found ${finalActiveCheck.length} active floor plans after copy. Active plans:`, 
        finalActiveCheck.map(p => `${p.id} (${p.name})`).join(', '));
      
      // Emergency fix - deactivate all except the one we want
      const { error: emergencyFixError } = await supabase
        .from("floor_plans")
        .update({ is_active: false })
        .eq("user_id", userId)
        .neq("id", data.id);
      
      if (emergencyFixError) {
        console.error('Error in emergency fix:', emergencyFixError);
      } else {
        console.log('Emergency fix applied: Deactivated all other floor plans');
      }
    } else if (finalActiveCheck && finalActiveCheck.length === 1) {
      console.log(`CONFIRMATION: Exactly one floor plan is active: ${finalActiveCheck[0].id} (${finalActiveCheck[0].name})`);
    } else {
      console.error('WARNING: No active floor plans found after copy process!');
      
      // Last resort activation
      const { error: lastResortError } = await supabase
        .from("floor_plans")
        .update({ is_active: true })
        .eq("id", data.id)
        .eq("user_id", userId);
      
      if (lastResortError) {
        console.error('Error in last resort activation:', lastResortError);
      } else {
        console.log('Last resort activation completed');
      }
    }
    
    if (!isVerified) {
      console.error(`WARNING: Could not verify copied floor plan activation after ${maxVerifyAttempts} attempts`);
    }
    
    return data;
  } catch (error) {
    console.error('Error in copyAndActivateSharedFloorPlan:', error);
    throw error;
  }
};
